import type { UserProfile } from '@/api'

// 如果宽高太窄，客服脚本不会正常初始化
const initStyle = `
      position: fixed;
      top: -9999px;
      left: -9999px;
      width: 100%;
      height: 100%;
      border: none;
      opacity: 0;
      pointer-events: none;
    `

/**
 * 客服管理类
 * 通过 iframe 加载独立的客服页面，使用 postMessage 通信
 */
class CustomerServiceManager {
  private iframe: HTMLIFrameElement | null = null
  private isReady = false
  private _isOpen = false
  private pendingMessages: Array<{ type: string; payload?: any }> = []

  /**
   * 初始化客服 iframe
   */
  init() {
    if (this.iframe) {
      console.warn('[CS Manager] Already initialized')
      return
    }

    // 创建隐藏的 iframe
    this.iframe = document.createElement('iframe')
    this.iframe.src = '/cs.html'
    this.iframe.style.cssText = initStyle
    this.iframe.title = 'Customer Service'

    // 监听来自 iframe 的消息
    window.addEventListener('message', this.handleMessage.bind(this))

    // 添加到页面
    document.body.appendChild(this.iframe)

    console.log('[CS Manager] Iframe created')
  }

  /**
   * 处理来自 iframe 的消息
   */
  private handleMessage(event: MessageEvent) {
    // 安全检查
    if (!event.data || typeof event.data !== 'object') {
      return
    }

    const { type, payload } = event.data

    switch (type) {
      case 'CS_LOADED':
        console.log('[CS Manager] CS page loaded')
        break

      case 'CS_READY':
        console.log('[CS Manager] CS ready')
        this.isReady = true
        // 发送所有待发送的消息
        this.flushPendingMessages()
        break

      case 'CS_OPENED':
        // 不知道什么原因会误触发，先注释掉
        // this._isOpen = true
        console.log('[CS Manager] CS opened')
        break

      case 'CS_CLOSED':
        this._isOpen = false
        this.iframe!.style.cssText = initStyle
        console.log('[CS Manager] CS closed')
        break
    }
  }

  /**
   * 向 iframe 发送消息
   */
  private postMessage(type: string, payload?: any) {
    if (!this.iframe || !this.iframe.contentWindow) {
      console.error('[CS Manager] Iframe not ready')
      return
    }

    // 如果还未准备好，加入待发送队列
    if (!this.isReady && type !== 'CS_INIT') {
      this.pendingMessages.push({ type, payload })
      return
    }

    this.iframe.contentWindow.postMessage(
      {
        type,
        payload,
      },
      '*', // 在生产环境中应该指定具体的 origin
    )
  }

  /**
   * 发送所有待发送的消息
   */
  private flushPendingMessages() {
    while (this.pendingMessages.length > 0) {
      const message = this.pendingMessages.shift()
      if (message) {
        this.postMessage(message.type, message.payload)
      }
    }
  }

  /**
   * 设置用户信息
   */
  setUserInfo(userProfile: UserProfile | undefined) {
    if (!userProfile) {
      return
    }

    const userInfo = {
      id: userProfile.id,
      name: userProfile.nickname,
      nickname: userProfile.nickname,
      email: undefined, // 如果有邮箱字段，可以添加
      phone: undefined, // 如果有手机号字段，可以添加
      kyc_verified: userProfile.kyc_verified,
      role: userProfile.role,
      lang: 'zh-CN',
    }

    this.postMessage('CS_SET_USER_INFO', userInfo)
    console.log('[CS Manager] User info sent')
  }

  /**
   * 清除用户信息
   */
  clearUserInfo() {
    this.postMessage('CS_CLEAR_USER_INFO')
    console.log('[CS Manager] User info cleared')
  }

  /**
   * 打开客服
   */
  open() {
    this.iframe!.style.cssText = `position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: none;
      z-index:999;
      pointer-events: auto;`

    this.postMessage('CS_OPEN')
    this._isOpen = true
  }

  /**
   * 关闭客服
   */
  close() {
    this.postMessage('CS_CLOSE')
    this._isOpen = false
  }

  /**
   * 客服是否当前已打开
   */
  get isOpen() {
    return this._isOpen
  }

  /**
   * 销毁 iframe
   */
  destroy() {
    if (this.iframe && this.iframe.parentNode) {
      this.iframe.parentNode.removeChild(this.iframe)
      this.iframe = null
      this.isReady = false
      this.pendingMessages = []
      console.log('[CS Manager] Destroyed')
    }
  }
}

// 创建单例
export const csManager = new CustomerServiceManager()

/**
 * 初始化客服管理器（在应用启动时调用）
 */
export function initCustomerService() {
  csManager.init()
}

/**
 * 设置客服访客信息
 */
export function setCustomerServiceUserInfo(userProfile: UserProfile | undefined) {
  csManager.setUserInfo(userProfile)
}

/**
 * 清除客服访客信息
 */
export function clearCustomerServiceUserInfo() {
  csManager.clearUserInfo()
}

/**
 * 打开客服窗口
 */
export function openCustomerService() {
  csManager.open()
}

/**
 * 关闭客服窗口
 */
export function closeCustomerService() {
  csManager.close()
}
